'use client'

/**
 * MockToolbar
 *
 * Development tool to easily set or remove the `x-mock-instructions` cookie via a UI.
 * This cookie adds a header to every GraphQL request, enabling backend mock responses
 * for testing the app in various scenarios.
 *
 * - Enter a mock ID and JSON parameters to activate mocks.
 * - Leave both fields empty to remove the cookie and disable mocks.
 * - The page reloads automatically after changes.
 *
 * Intended for use in development environments only.
 * https://descours-cabaud.atlassian.net/browse/ECOM-18066
 */

'use client'

import { Button } from '@/ui/buttons/button/Button.client'
import { Form } from '@/ui/form'
import { useForm } from '@/ui/form/Form.client'
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import { useEffect, useRef, useState } from 'react'
import { useOverlay } from 'react-aria'

export const MOCK_INSTRUCTIONS_HEADER_NAME = 'x-mock-instructions'

type MockToolbarFormData = {
  /** Mock ID */
  id: string
  /** Parameters for the mock */
  params: string
}

/**
 * {@link https://descours-cabaud.atlassian.net/wiki/spaces/magento2/pages/20802174977/Outil+de+Mock+Front+MockToolbar, 🔗 See docs page on Confluence}
 */
export default function MockToolbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const form = useForm<MockToolbarFormData>({
    defaultValues: {
      id: '',
      params: '',
    },
  })

  const { overlayProps } = useOverlay(
    {
      isOpen,
      onClose: () => setIsOpen(false),
      shouldCloseOnBlur: true,
      isDismissable: true,
    },
    containerRef,
  )

  useEffect(() => {
    const cookieValues = getCookie(MOCK_INSTRUCTIONS_HEADER_NAME)

    if (cookieValues) {
      try {
        const parsedValues = JSON.parse(String(cookieValues))
        const { id, args } = parsedValues[0] || {}

        form.setValue('id', id || '')
        form.setValue('params', JSON.stringify(args) || '')
      } catch (error) {
        console.error('Error parsing cookie values:', error)
      }
    }
  }, [form])

  const handleSubmit = ({ id, params }: MockToolbarFormData) => {
    if (!id.trim() && !params.trim()) {
      return
    }

    setIsLoading(true)

    const mockCookieValue = JSON.stringify([{ id, args: JSON.parse(params) }])

    setCookie(MOCK_INSTRUCTIONS_HEADER_NAME, mockCookieValue, {
      maxAge: 60 * 60 * 24 * 7, // 7 days
    })

    window.location.reload()
  }

  const handleOnDeleteMocks = () => {
    if (!confirm('Are you sure you want to delete all mocks?')) {
      return
    }

    setIsLoading(true)
    deleteCookie(MOCK_INSTRUCTIONS_HEADER_NAME)
    window.location.reload()
  }

  return (
    <div {...overlayProps} ref={containerRef} className="fixed bottom-[20px] left-9 z-toast">
      <button
        // FIXME: replace hover: by useHover()
        // eslint-disable-next-line dc/no-hover
        className="flex size-[38px] items-center justify-center rounded-full border-2 border-grey-800 bg-grey-800 text-lg text-white hover:border-black hover:bg-grey-700"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Mock Toolbar Button"
      >
        M
      </button>
      {isOpen && (
        <div className="absolute bottom-7 left-0 z-toast rounded-sm bg-white p-2 shadow-xs">
          <Form form={form} onSubmitAction={handleSubmit} className="flex flex-col gap-1">
            <span>Instructions mocks</span>
            <div className="flex gap-1">
              <Form.Input.Text name="id" label="" placeholder="Mock ID" className="w-20 min-w-0" />
              <Form.Input.Text
                name="params"
                label=""
                placeholder="Params"
                className="w-20 min-w-0"
              />
            </div>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="secondary"
                size="md"
                takesParentWidth
                isLoading={isLoading}
                onPress={handleOnDeleteMocks}
              >
                Supprimer les mocks
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                takesParentWidth
                isLoading={isLoading}
              >
                Enregistrer
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  )
}
